import type { ReactElement } from "react";
import type { ModerationAction } from "odyssey-shared";
import { Button, EmptyState, Glyph, type GlyphName } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { accessSentence } from "../feedback/words.ts";
import { LengthField, ReasonBox, type FormText } from "./action-form.tsx";
import type { FamilySnapshot } from "./family.ts";

/**
 * The Moderation window's Feedback tab (ADR-081's amendment of 2026-10-05; ADR-056's amendment of 2026-10-06,
 * decision 14; the design's `moderation-window` card, a visual reference): the chosen family's access to feedback,
 * kept apart from the actions: its state, what the player sees on opening Feedback (the game menu's own sentence),
 * and the changes the state allows and the gateway offered. Every change asks for a staff-only reason, which is the
 * action's reason on the wire; only Pause asks for a length.
 */

/**
 * The feedback actions, by their place on the tab.
 */
type FeedbackAction = "feedbackpause" | "feedbackresume" | "feedbackoff" | "feedbackon";

/**
 * Which changes each state allows, in the card's order.
 */
const CHANGES: Readonly<Record<"open" | "paused" | "off", readonly FeedbackAction[]>> = { open: ["feedbackpause", "feedbackoff"], paused: ["feedbackresume", "feedbackoff"], off: ["feedbackon"] };

/**
 * The tab's body.
 *
 * @param props The family's read; the actions offered and the one chosen; the form's text and `onText`; whether a
 *   request is `busy`; whether the window is `narrow`; the clock and the writer of moments; when the pause would
 *   end, or null; and `onChoose`.
 * @returns The body.
 */
export function FeedbackTab({ family, offered, chosen, text, onText, busy, narrow, now, when, ends, onChoose }: { readonly family: FamilySnapshot; readonly offered: readonly ModerationAction[]; readonly chosen: ModerationAction | null; readonly text: FormText; readonly onText: (text: Partial<FormText>) => void; readonly busy: boolean; readonly narrow: boolean; readonly now: number; readonly when: (at: number) => string; readonly ends: string | null; readonly onChoose: (action: ModerationAction) => void }): ReactElement {
  const read = family.read;
  if (family.name === null || read === null) {
    return (
      <EmptyState glyph="user" title={say("moderation.window.pickTitle")}>
        {narrow ? say("moderation.window.pickNarrowTo", { purpose: { k: "moderation.window.pickFeedback" } }) : say("moderation.window.pickWideTo", { purpose: { k: "moderation.window.pickFeedback" } })}
      </EmptyState>
    );
  }
  const access = read.feedback;
  const glyph: GlyphName = access.state === "open" ? "check" : access.state === "paused" ? "clock" : "lock";
  const state = access.state === "paused" ? say("moderation.window.feedbackPausedUntil", { when: when(access.until) }) : say(`moderation.window.feedbackStates.${access.state}`);
  const sees = accessSentence(access, now);
  const changes = CHANGES[access.state].filter((action) => offered.includes(action));
  const asked = chosen !== null && (CHANGES[access.state] as readonly ModerationAction[]).includes(chosen) ? (chosen as FeedbackAction) : null;
  return (
    <div className="ot-moderation__feedback-tab" data-moderation="feedback">
      <div className="ot-moderation__feedback-head">
        <span className="ot-moderation__accent">{say("moderation.window.feedbackHeading")}</span>
        <span className="ot-moderation__muted ot-moderation__small">{say("moderation.window.feedbackFor", { family: read.family })}</span>
      </div>
      <div className="ot-moderation__feedback-state ot-panel ot-ui-frame-panel" data-moderation="feedback-access">
        <span className={`ot-moderation__stand-mark ot-moderation__stand-mark--${access.state}`}>
          <Glyph name={glyph} />
        </span>
        <span className="ot-moderation__stand-text">
          <span className={`ot-moderation__stand-what ot-moderation__feedback--${access.state}`}>{state}</span>
          {access.state === "open" ? <span className="ot-moderation__muted ot-moderation__small">{say("moderation.window.feedbackOpen", { family: read.family })}</span> : null}
        </span>
      </div>
      <div className="ot-moderation__sees">
        <span className="ot-moderation__muted ot-moderation__label">
          <span className="ot-moderation__hint-mark">
            <Glyph name="eye-small" />
          </span>
          {say("moderation.window.feedbackSees", { family: read.family })}
        </span>
        <span className={sees === null ? "ot-moderation__sees-box ot-moderation__muted" : "ot-moderation__sees-box"}>{sees === null ? say("moderation.window.feedbackNormal") : say("moderation.window.quoted", { text: sees })}</span>
      </div>
      {asked === null ? (
        <div className="ot-moderation__act-row">
          {changes.map((action) =>
            action === "feedbackoff" ? (
              <Button key={action} variant="danger" disabled={busy || !read.actable} onClick={() => onChoose(action)}>
                {say(`moderation.window.feedbackButtons.${action}`)}
              </Button>
            ) : (
              <Button key={action} disabled={busy || !read.actable} onClick={() => onChoose(action)}>
                {say(`moderation.window.feedbackButtons.${action}`)}
              </Button>
            ),
          )}
        </div>
      ) : (
        <>
          {asked === "feedbackpause" ? <LengthField action="feedbackpause" value={text.length} onChange={(length) => onText({ length })} disabled={busy} ends={ends} /> : null}
          <ReasonBox kind="staff" label={say("moderation.window.feedbackReason")} value={text.reason} onChange={(reason) => onText({ reason })} disabled={busy} />
        </>
      )}
      <p className="ot-moderation__muted ot-moderation__small">{say("moderation.window.feedbackNote")}</p>
    </div>
  );
}
